<template>
  <ak-modal-body class="overflow-visible">
    <h3 class="mb-tiny color-primary">
      {{ modalTitle }}
    </h3>

    <ak-notification
      v-if="hasError"
      type="danger"
      has-icon
      class="mt-small mb-small"
    >
      Please complete all required fields.
    </ak-notification>

    <template v-if="isLoading">
      <div class="min-h-350 one flex flex--center flex--justify-center">
        <ak-loading-spinner />
      </div>
    </template>

    <template v-if="!isLoading">
      <template v-if="isFolder || !showAgencyOnly">
        <div class="min-h-350">
          <ak-field :label="titleLabel" class="mt-small" required>
            <template #helper>
              This can be a more client friendly name.
            </template>
            <ak-input
              ref="title"
              v-model="computedModel.title"
              required
              :custom-rules="customValidationRules"
            />
          </ak-field>

          <ak-field label="Parent Folder">
            <ak-select
              track-by="_id"
              label="title"
              :model-value="selectedFolder"
              :options="folders"
              :custom-label="nameWithParent"
              :custom-rules="folderCustomRules"
              @select="selectFolder"
            />
          </ak-field>

          <ak-field v-if="isFolder" label="Description">
            <ak-input v-model="computedModel.description" type="textarea" />
          </ak-field>
        </div>
      </template>

      <template v-else>
        <ak-tabs v-model="currentTab" visible-overflow in-panel border-bottom>
          <ak-tab-item label="Project" class="min-h-350 pt-small">
            <ak-field :label="titleLabel" class="mt-small" required>
              <template #helper>
                This can be a more client friendly name.
              </template>
              <ak-input
                ref="title"
                v-model="computedModel.title"
                required
                :custom-rules="customValidationRules"
              />
            </ak-field>

            <ak-field label="Parent Folder">
              <ak-select
                track-by="_id"
                label="title"
                :model-value="selectedFolder"
                :options="folders"
                :custom-label="nameWithParent"
                :custom-rules="folderCustomRules"
                @select="selectFolder"
              />
            </ak-field>
          </ak-tab-item>

          <ak-tab-item label="Campaign" class="min-h-350 pt-small">
            <ak-field label="Campaign ID" required class="mt-small">
              <template #helper>
                The campaign ID is brought from the Media Plan.
              </template>
              <ak-input v-model="mediaPlan.uid" disabled />
            </ak-field>

            <ak-field label="Campaign Description">
              <template #helper>
                Give your clients a brief overview on the main aims of this
                campaign.
              </template>
              <ak-input v-model="computedModel.description" type="textarea" />
            </ak-field>

            <ak-field>
              <ak-tooltip class="pull-right">
                <template #trigger>
                  <ak-tag ghost color="primary">Agency Only</ak-tag>
                </template>
                Only visible to Agency Users
              </ak-tooltip>
              <ak-checkbox
                v-model="computedModel.akeroAdvertising.enabled"
                label
              >
                Run via Guaranteed Pricing Model
              </ak-checkbox>
            </ak-field>
          </ak-tab-item>

          <ak-tab-item label="Dashboard" class="min-h-350 pt-small">
            <ak-field label="Dashboard Name">
              <template #helper>
                The dashboard name is brought from the Media Plan.
              </template>
              <ak-input v-model="mediaPlanName" disabled />
            </ak-field>
            <ak-field label="Plan ID">
              <template #helper>
                The Plan ID will be automatically generated using the ID from
                the Akero Media Plan. You'll manually need to input this
                information for legacy plans.
              </template>
              <ak-input
                v-model="computedModel.campaignName"
                :custom-rules="planValidationRules"
                @blur="setDisabledInputs"
              />
            </ak-field>

            <ak-field>
              <ak-checkbox
                v-model="dateRangeEnabled"
                label
                :disabled="disableDashboardInputs"
                @change="setRangeEnabled"
              >
                <strong>Set Default Dashboard Date Range</strong>
              </ak-checkbox>
            </ak-field>

            <template v-if="dateRangeEnabled">
              <ak-field>
                <ak-date-picker
                  v-model="selectedDate"
                  :config="pickerConfig"
                  @change="setDateRangeValues"
                />
              </ak-field>
            </template>

            <ak-box
              v-if="!disableDashboardInputs"
              narrow
              background="grey-lightest"
            >
              <ak-field label="Main KPI" required>
                <ak-select
                  v-model="selectedKpi"
                  required
                  :options="kpiOptions"
                  track-by="value"
                  label="label"
                  @select="selectKPI"
                />
              </ak-field>

              <ak-field label="Campaign Proposal">
                <ak-input
                  v-model="computedModel.campaignProposal"
                  type="url"
                  pattern="https://.*"
                  placeholder="https://example.com/proposal.pdf"
                />
              </ak-field>
              <ak-field label="MCA - Mid Campaign Analysis">
                <ak-input
                  v-model="computedModel.midCampaignAnalysis"
                  type="url"
                  pattern="https://.*"
                  placeholder="https://example.com/mid-campaign.pdf"
                />
              </ak-field>
              <ak-field label="PCA - Post Campaign Analysis">
                <ak-input
                  v-model="computedModel.postCampaignAnalysis"
                  type="url"
                  pattern="https://.*"
                  placeholder="https://example.com/post-campaign.pdf"
                />
              </ak-field>
            </ak-box>
          </ak-tab-item>

          <ak-tab-item label="Team" class="pt-small min-h-350">
            <ak-project-team-select
              :team="computedModel.team"
              @change="updatedTeam"
            >
              <p>
                Add Natives and client users who are part of the team involved
                in this campaign. These users will be kept up-to-date with
                notifications whenever there are new campaign recommendations to
                read.
              </p>
            </ak-project-team-select>
          </ak-tab-item>

          <ak-tab-item
            label="Conversions"
            class="pt-small min-h-350 conversions"
          >
            <ak-field>
              <template #helper>
                Specify which conversions to include in the conversions report.
              </template>
              <ak-radio-group
                v-model="conversionType"
                :options="radioOptions"
                name="conversionType"
              />
            </ak-field>

            <ak-field addons>
              <template #helper>
                Please specify conversion names to allow list. All other named
                conversions will be excluded.
              </template>
              <ak-input v-model="newConversion" />
              <ak-button @click.prevent="addConversion"
                >Add Conversion</ak-button
              >
            </ak-field>

            <template v-if="conversionType === 'whitelist'">
              <div v-for="item in whitelist" :key="item">
                <ak-box
                  narrow
                  rounded
                  background="grey-lightest"
                  class="mb-tiny"
                >
                  <strong>{{ item }}</strong>
                  <ak-button
                    class="pull-right"
                    size="small"
                    color="transparent"
                    circle
                    @click="removeConversion(item)"
                  >
                    <ak-icon type="trash" color="danger" />
                  </ak-button>
                </ak-box>
              </div>
            </template>

            <template v-if="conversionType === 'blacklist'">
              <div v-for="item in blacklist" :key="item">
                <ak-box
                  narrow
                  rounded
                  background="grey-lightest"
                  class="mb-tiny"
                >
                  <strong>{{ item }}</strong>
                  <ak-button
                    class="pull-right"
                    size="small"
                    color="transparent"
                    circle
                    @click="removeConversion(item)"
                  >
                    <ak-icon type="trash" color="danger" />
                  </ak-button>
                </ak-box>
              </div>
            </template>
          </ak-tab-item>
        </ak-tabs>
      </template>
    </template>
  </ak-modal-body>

  <ak-modal-footer class="radius radius-top-none">
    <ak-modal-button type="submit" :disabled="isDisabled">
      <span v-if="isFolder">Save Project Folder</span>
      <span v-else>Save Project</span>
    </ak-modal-button>
  </ak-modal-footer>
</template>

<script>
import ModalBody from '@akerolabs/akero-vue/src/components/modal/ModalBody.vue'
import ModalFooter from '@akerolabs/akero-vue/src/components/modal/ModalFooter.vue'
import ModalButton from '@akerolabs/akero-vue/src/components/modal/ModalButton.vue'
import LoadingSpinner from '@akerolabs/akero-vue/src/components/loading/LoadingSpinner.vue'
import Tabs from '@akerolabs/akero-vue/src/components/tabs/Tabs.vue'
import TabItem from '@akerolabs/akero-vue/src/components/tabs/TabItem.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import Checkbox from '@akerolabs/akero-vue/src/components/checkbox/Checkbox.vue'
import Tooltip from '@akerolabs/akero-vue/src/components/tooltip/Tooltip.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import RadioGroup from '@akerolabs/akero-vue/src/components/radioGroup/RadioGroup.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import Notification from '@akerolabs/akero-vue/src/components/notification/Notification.vue'
import date from '@akerolabs/akero-vue/src/utils/date'
import { display } from '@akerolabs/akero-vue/src/services/users'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { merge } from '@akerolabs/akero-vue/src'
import { accountObj, agencyObj } from '@akerolabs/akero-vue/src/store'
import { LocalisationFactory } from '@akerolabs/akero-vue/src/services/localisation'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src'
import { mapFolders, checkNetsuiteCampaignName } from '../../utils/projects'
import ProjectTeamSelect from './ProjectTeamSelect.vue'
import { uniqueValidtion } from '../../utils/validationRules'
import { defineComponent, onMounted, ref, computed, inject } from 'vue'

const tabs = {
  project: 0,
  campaign: 1,
  dashboard: 2,
  additionalDetails: 2,
  team: 3,
  conversion: 4,
  conversions: 4,
}

export default defineComponent({
  name: 'AkCreateProjectModal',

  components: {
    [ModalBody.name]: ModalBody,
    [ModalFooter.name]: ModalFooter,
    [ModalButton.name]: ModalButton,
    [LoadingSpinner.name]: LoadingSpinner,
    [Tabs.name]: Tabs,
    [TabItem.name]: TabItem,
    [ProjectTeamSelect.name]: ProjectTeamSelect,
    [Field.name]: Field,
    [Input.name]: Input,
    [Checkbox.name]: Checkbox,
    [Tooltip.name]: Tooltip,
    [Button.name]: Button,
    [RadioGroup.name]: RadioGroup,
    [Icon.name]: Icon,
    [Notification.name]: Notification,
  },

  props: {
    modelValue: {
      type: Object,
      default: () => ({}),
    },

    isFolder: {
      type: Boolean,
      default: false,
    },

    hasMediaPlanLink: {
      type: Boolean,
      default: false,
    },

    maxDepth: {
      type: Number,
      default: 1,
    },

    activeTab: {
      type: String,
      default: 'project',
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const localisation = LocalisationFactory()
    const modalTitle = `Edit Project ${props.isFolder ? 'Folder' : ''}`
    const titleLabel = props.isFolder ? 'Folder Name' : 'Project Name'
    const isLoading = ref(true)
    const folders = ref([])
    const selectedFolder = ref(undefined)
    const mediaPlan = ref({ uid: '—' })
    const selectedKpi = ref('')
    const disableDashboardInputs = ref(true)
    const dateRangeEnabled = ref(false)
    const selectedDate = ref('')
    const conversionDashboardsEnabled = ref(false)
    const radioOptions = ref(['whitelist', 'blacklist'])
    const newConversion = ref('')
    const form = inject('akForm')
    const hasError = ref(false)
    const isDisabled = ref(false)
    const currentTab = ref(tabs[props.activeTab])

    const showAgencyOnly = display(
      'show',
      'developer,superadmin,agencyadmin,agencyuser',
    )

    const computedModel = computed({
      get() {
        console.log('project', props.modelValue)
        return props.modelValue
      },
      set(val) {
        console.log('project', val)
        emit('update:modelValue', val)
      },
    })

    const conversionType = ref(computedModel.value.conversions.type)
    const whitelist = ref(computedModel.value.conversions.whitelist)
    const blacklist = ref(computedModel.value.conversions.blacklist)

    const selectedDateRange = ref([
      date().utc().startOf('year').format(),
      date().utc().endOf('day').format(),
    ])

    const kpiOptions = ref([
      { value: 'Clicks', label: 'Clicks' },
      { value: 'Cost Per Click', label: 'Cost Per Click' },
      { value: 'CTR', label: 'CTR' },
      { value: 'Average Position', label: 'Average Position' },
      { value: 'Leads', label: 'Leads' },
      { value: 'Cost Per Lead', label: 'Cost Per Lead' },
      { value: 'Impressions', label: 'Impressions' },
      { value: 'Page Views', label: 'Page Views' },
      { value: 'Landing Page Views', label: 'Landing Page Views' },
      { value: 'Viewability', label: 'Viewability' },
      { value: 'Conversions', label: 'Conversions' },
      { value: 'Video ThruPlays', label: 'Video ThruPlays' },
    ])

    const selectKPI = (kpi) => {
      computedModel.value.mainKPI = kpi.value
    }

    const setDisabledInputs = () => {
      disableDashboardInputs.value = !computedModel.value.campaignName
    }

    const pickerConfig = ref({
      defaultDate: selectedDateRange.value,
      presetRanges: true,
      altInput: true,
      altFormat: localisation.getLocalisationPreferences().flatPickrFormat,
    })

    const setDateRangeValues = (values) => {
      const [start, end] = values
      const startStr = date(start).utc().format()
      const endStr = date(end).utc().format()

      selectedDateRange.value[0] = startStr
      selectedDateRange.value[0] = endStr

      computedModel.value.defaultDashboardDateRange.start = startStr
      computedModel.value.defaultDashboardDateRange.end = endStr
      computedModel.value.start = startStr
      computedModel.value.end = endStr
    }

    const mediaPlanName = computed(() => {
      return (
        mediaPlan.value && mediaPlan.value.data && mediaPlan.value.data.name
      )
    })

    const updatedTeam = (team) => {
      computedModel.value.team = team
    }

    const selectFolder = (folder) => {
      computedModel.value.parent = folder && folder._id ? folder._id : null
      selectedFolder.value = folder
    }

    const setRangeEnabled = () => {
      computedModel.value.defaultDashboardDateRange.enabled =
        dateRangeEnabled.value
    }

    const nameWithParent = (item) => {
      if (!item.parent) return item.title

      const parent = folders.value.find((i) => {
        return i._id === item.parent
      })

      if (typeof parent === 'object') {
        return `${parent.title} → ${item.title}`
      }

      return item.title
    }

    const getConversions = () => {
      return computedModel.value.conversions[
        computedModel.value.conversions.type
      ]
    }

    const addConversion = () => {
      if (newConversion.value.length) {
        if (conversionType.value === 'blacklist') {
          blacklist.value.push(newConversion.value)
        } else {
          whitelist.value.push(newConversion.value)
        }
        newConversion.value = ''
      }
    }

    const removeConversion = (conversion) => {
      let list = conversionType.value === 'blacklist' ? blacklist : whitelist
      list.value = list.value.filter((item) => item !== conversion)
    }

    const customValidationRules = [
      uniqueValidtion({
        message: 'Name has already been used',
        type: 'campaign',
        account: () => accountObj()._id,
        agency: () => agencyObj()._id,
        current: () => computedModel.value._id,
        property: 'title',
      }),
    ]

    const folderCustomRules = [
      async (input) => {
        if (
          selectedFolder.value &&
          selectedFolder.value.depth >= props.maxDepth &&
          props.isFolder
        ) {
          input.setCustomValidity(
            'Maximum allowed depth of sub-folders reached.',
          )
          return false
        } else {
          input.setCustomValidity('')
          return true
        }
      },
    ]

    const planValidationRules = [
      async (input) => {
        const dupeCampaignName = await checkNetsuiteCampaignName(
          computedModel.value,
        )
        if (dupeCampaignName) {
          input.setCustomValidity('Dashboard filter name is already in use')
          return false
        } else {
          input.setCustomValidity('')
          return true
        }
      },
    ]

    const getFolders = async () => {
      const { data } = await request.get(`/campaigns/folders`, {
        params: {
          account: accountObj()._id,
          agency: agencyObj()._id,
          per_page: 0,
          archived: JSON.stringify({ $ne: true }),
        },
      })

      const mapped = mapFolders(undefined, data.data)
      const blank = { title: '-', _id: undefined }

      mapped.unshift(blank)

      folders.value = mapped.filter((i) => {
        return i._id !== computedModel.value._id
      })

      selectFolder(
        mapped.find((i) => {
          return i._id === computedModel.value.parent
        }),
      )
    }

    const getMediaPlan = async () => {
      try {
        const { data: res } = await request.get(
          `/campaigns/${computedModel.value._id}`,
          {
            params: {
              account: accountObj()._id,
              agency: agencyObj()._id,
              populate: 'akeroAdvertising.quote',
            },
          },
        )

        mediaPlan.value =
          res.data &&
          res.data.akeroAdvertising &&
          res.data.akeroAdvertising.quote

        mediaPlan.value.uid = mediaPlan.value.uid
          ? `C#${mediaPlan.value.uid}`
          : mediaPlan.value.uid
      } catch (ex) {
        mediaPlan.value = {
          uid: undefined,
        }
      }
    }

    const mergeResponse = async (updatedProject) => {
      return new Promise((resolve) => {
        merge(computedModel.value, updatedProject, false, ['$$hashKey'])
        setTimeout(() => {
          resolve(updatedProject)
        })
      })
    }

    form.onValidationError(() => {
      hasError.value = true
    })

    form.onBeforeSubmit(async () => {
      isDisabled.value = true
      computedModel.value.conversions.type = conversionType.value
      computedModel.value.conversions.blacklist = blacklist.value
      computedModel.value.conversions.whitelist = whitelist.value
      delete computedModel.value.$$hashKey

      const { data: res } = await request.put(
        `/campaigns/${computedModel.value._id}`,
        {
          ...computedModel.value,
        },
      )

      const { data, status } = res

      if (status === 'error') {
        ToastProgrammatic.open({
          duration: 5000,
          message: 'Failed to update the project please try again later',
          type: 'danger',
        })

        isDisabled.value = false
        return false
      }

      mergeResponse(data)

      return true
    })

    onMounted(async () => {
      const account = accountObj()
      await getFolders()
      await getMediaPlan()

      disableDashboardInputs.value = props.modelValue.campaignName
        ? false
        : true

      conversionDashboardsEnabled.value =
        account.campaignsDashboards.conversionDashboards !== 'disabled'

      dateRangeEnabled.value =
        props.modelValue.defaultDashboardDateRange.enabled

      if (props.modelValue.mainKPI) {
        selectedKpi.value = kpiOptions.value.find((i) => {
          return i.value === props.modelValue.mainKPI
        })
      }

      isLoading.value = false
    })

    return {
      modalTitle,
      isLoading,
      showAgencyOnly,
      computedModel,
      currentTab,
      updatedTeam,
      titleLabel,
      customValidationRules,
      selectedFolder,
      nameWithParent,
      folderCustomRules,
      selectFolder,
      getFolders,
      folders,
      mediaPlan,
      selectedKpi,
      kpiOptions,
      selectKPI,
      setDisabledInputs,
      disableDashboardInputs,
      dateRangeEnabled,
      planValidationRules,
      pickerConfig,
      selectedDate,
      setDateRangeValues,
      selectedDateRange,
      mediaPlanName,
      conversionDashboardsEnabled,
      radioOptions,
      addConversion,
      newConversion,
      getConversions,
      removeConversion,
      whitelist,
      blacklist,
      conversionType,
      hasError,
      isDisabled,
      setRangeEnabled,
    }
  },
})
</script>

<style lang="scss">
@import '@akerolabs/akero-vue/src/scss/_settings.scss';
.conversions {
  .ak-form-group-wrap:first-child {
    .ak-form-group__label {
      .ak-form-helper {
        margin-bottom: 0.75rem;
      }
    }
    .ak-form-group__body {
      .ak-radio-group {
        label {
          &:first-child {
            margin-right: 0.75rem;
          }
          padding: 0.75rem;
          cursor: pointer;
          border: 2px solid #d3d5de;
          border-radius: 5px;
          &:hover {
            border-color: $color-primary;
            box-shadow: 0 0 15px rgba(52, 75, 249, 0.5);
          }
          .ak-radio__body {
            text-transform: capitalize;
            padding-left: 0.75rem;
          }
        }
        label:has(input:checked + .ak-radio__replacement) {
          border-color: $color-primary !important;
          box-shadow: 0 0 15px rgba(52, 75, 249, 0.5);
          .ak-radio__body {
            color: $color-primary !important;
          }
        }
      }
    }
  }
}
</style>
